import React, { useState, useEffect } from 'react';
import { Lightbulb, CheckCircle, AlertCircle, MessageSquare, ArrowRight } from 'lucide-react';

type Option = {
  id: string;
  text: string;
  consequences: string;
  score: number;
};

type Scenario = {
  id: string;
  title: string;
  description: string;
  careerPath: string;
  options: Option[];
  marketData: string;
};

type AIDecisionMakingProps = {
  careerPath: string;
  onChatWithMentor?: () => void;
};

const AIDecisionMaking: React.FC<AIDecisionMakingProps> = ({ careerPath, onChatWithMentor }) => {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [currentScenario, setCurrentScenario] = useState<Scenario | null>(null);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simular carga de escenarios desde una API
    setTimeout(() => {
      setScenarios(generateScenariosForCareerPath(careerPath));
      setLoading(false);
    }, 1000);
  }, [careerPath]);

  useEffect(() => {
    if (scenarios.length > 0 && !currentScenario) {
      setCurrentScenario(scenarios[0]);
    }
  }, [scenarios, currentScenario]);

  const generateScenariosForCareerPath = (path: string): Scenario[] => {
    const commonScenarios: Scenario[] = [
      {
        id: 'common-1',
        title: 'Conflicto en el equipo',
        description: 'Dos miembros clave de tu equipo están en desacuerdo sobre la dirección de un proyecto importante. La tensión está afectando la productividad del equipo entero. ¿Cómo manejarías esta situación?',
        careerPath: 'common',
        options: [
          {
            id: 'c1-a',
            text: 'Reunirte individualmente con cada persona para entender sus perspectivas antes de facilitar una conversación conjunta',
            consequences: 'Este enfoque permite entender las preocupaciones de cada parte y preparar una mediación efectiva. Demuestra respeto por ambas perspectivas y aumenta las probabilidades de una resolución duradera.',
            score: 9
          },
          {
            id: 'c1-b',
            text: 'Organizar una reunión de equipo para discutir abiertamente el conflicto y buscar una solución colectiva',
            consequences: 'Una discusión abierta puede ser útil, pero sin preparación previa podría intensificar el conflicto. Este enfoque funciona mejor cuando el conflicto no es demasiado personal.',
            score: 6
          },
          {
            id: 'c1-c',
            text: 'Tomar una decisión ejecutiva sobre la dirección del proyecto para resolver el conflicto rápidamente',
            consequences: 'Aunque resuelve el problema a corto plazo, no aborda las causas subyacentes del conflicto y podría generar resentimiento. Además, pierdes la oportunidad de aprovechar perspectivas valiosas.',
            score: 3
          }
        ],
        marketData: 'Los equipos que resuelven conflictos de manera colaborativa son un 33% más productivos que aquellos donde los líderes imponen decisiones.'
      }
    ];

    const specificScenarios: Record<string, Scenario[]> = {
      'azure-admin': [
        {
          id: 'azure-1',
          title: 'Incidente de seguridad',
          description: 'Se ha detectado una brecha de seguridad en una red virtual. Los sistemas críticos podrían estar comprometidos.',
          careerPath: 'azure-admin',
          options: [
            {
              id: 'azure1-a',
              text: 'Aislar inmediatamente la red virtual afectada y activar el protocolo de respuesta a incidentes',
              consequences: 'Esta respuesta rápida minimiza el impacto potencial de la brecha y sigue las mejores prácticas de seguridad, aunque puede causar una interrupción temporal del servicio.',
              score: 9
            },
            {
              id: 'azure1-b',
              text: 'Investigar el alcance de la brecha mientras se mantienen los servicios funcionando',
              consequences: 'Mantener los servicios activos durante la investigación podría permitir que la brecha se expanda y comprometa más sistemas.',
              score: 4
            },
            {
              id: 'azure1-c',
              text: 'Restaurar inmediatamente desde el último backup conocido seguro',
              consequences: 'Aunque restaura un estado seguro conocido, esta acción podría resultar en pérdida de datos y no aborda la causa raíz de la brecha.',
              score: 6
            }
          ],
          marketData: 'El 83% de los incidentes de seguridad se resuelven más efectivamente cuando se toman medidas inmediatas de contención.'
        },
        {
          id: 'azure-2',
          title: 'Optimización de costos',
          description: 'Los costos de Azure han aumentado significativamente en el último trimestre. El CFO solicita una reducción inmediata.',
          careerPath: 'azure-admin',
          options: [
            {
              id: 'azure2-a',
              text: 'Implementar políticas de auto-scaling y reservas de instancias para optimizar costos a largo plazo',
              consequences: 'Esta solución proporciona ahorros sostenibles sin comprometer el rendimiento, aunque requiere una inversión inicial de tiempo y planificación.',
              score: 9
            },
            {
              id: 'azure2-b',
              text: 'Reducir manualmente los recursos asignados a todos los servicios',
              consequences: 'Aunque produce ahorros inmediatos, esta aproximación puede afectar el rendimiento y la disponibilidad de los servicios.',
              score: 5
            },
            {
              id: 'azure2-c',
              text: 'Realizar un análisis completo de la arquitectura y optimizar basándose en los patrones de uso',
              consequences: 'Este enfoque equilibrado identifica oportunidades de optimización sostenibles pero requiere más tiempo para mostrar resultados.',
              score: 7
            }
          ],
          marketData: 'Las empresas que implementan estrategias de optimización de costos automatizadas en Azure logran un ahorro promedio del 35% en sus gastos de nube.'
        }
      ],
      'product-manager': [
        {
          id: 'pm-1',
          title: 'Recorte de presupuesto',
          description: 'Tu presupuesto de desarrollo ha sido recortado un 30% debido a condiciones económicas. Tienes que decidir qué funcionalidades priorizar y cuáles posponer para el próximo trimestre.',
          careerPath: 'product-manager',
          options: [
            {
              id: 'pm1-a',
              text: 'Priorizar las funcionalidades con mayor impacto en la retención de usuarios, aunque sean más complejas de implementar',
              consequences: 'Enfocarse en la retención es una estrategia sólida a largo plazo. Aunque requiere más recursos inicialmente, estas funcionalidades suelen tener mayor ROI y fortalecen la base de usuarios existente.',
              score: 8
            },
            {
              id: 'pm1-b',
              text: 'Implementar el mayor número posible de funcionalidades pequeñas para mostrar progreso continuo',
              consequences: 'Esta estrategia puede dar la impresión de avance rápido, pero podría resultar en un producto con muchas funcionalidades superficiales que no resuelven problemas fundamentales de los usuarios.',
              score: 5
            },
            {
              id: 'pm1-c',
              text: 'Centrarse exclusivamente en funcionalidades que generen ingresos a corto plazo',
              consequences: 'Aunque puede ayudar con los problemas financieros inmediatos, este enfoque podría comprometer la experiencia del usuario y la retención a largo plazo, creando un ciclo negativo.',
              score: 6
            }
          ],
          marketData: 'Las empresas que mantienen el foco en la retención de usuarios durante períodos de restricción presupuestaria tienen un 45% más de probabilidades de recuperarse rápidamente.'
        }
      ],
      'developer': [
        {
          id: 'dev-1',
          title: 'Deuda técnica',
          description: 'Tu equipo está trabajando en un proyecto con un plazo ajustado. Has identificado una deuda técnica significativa que podría causar problemas en el futuro, pero abordarla ahora retrasaría la entrega.',
          careerPath: 'developer',
          options: [
            {
              id: 'dev1-a',
              text: 'Comunicar el problema al equipo y al product manager, proponiendo un plan para abordar la deuda técnica después de la entrega',
              consequences: 'Esta es una solución equilibrada que reconoce tanto las limitaciones de tiempo como la importancia de la calidad del código. Establece expectativas claras y un compromiso para resolver el problema.',
              score: 9
            },
            {
              id: 'dev1-b',
              text: 'Dedicar tiempo ahora a resolver la deuda técnica, aunque signifique retrasar la entrega',
              consequences: 'Aunque técnicamente es la mejor solución para la calidad del código, ignorar las restricciones de negocio puede tener consecuencias negativas para el equipo y la empresa.',
              score: 5
            },
            {
              id: 'dev1-c',
              text: 'Ignorar la deuda técnica por ahora y centrarse en cumplir con el plazo',
              consequences: 'Cumplir con el plazo es importante, pero ignorar completamente la deuda técnica sin un plan para abordarla posteriormente puede llevar a problemas mayores y más costosos en el futuro.',
              score: 3
            }
          ],
          marketData: 'El 83% de los desarrolladores reportan que la deuda técnica no gestionada reduce significativamente la productividad.'
        }
      ],
      'entrepreneur': [
        {
          id: 'ent-1',
          title: 'Aumento de precios del proveedor',
          description: 'Tu proveedor principal ha aumentado repentinamente los precios en un 20%. Este cambio afectará significativamente tus márgenes de beneficio.',
          careerPath: 'entrepreneur',
          options: [
            {
              id: 'ent1-a',
              text: 'Negociar con el proveedor ofreciendo un compromiso a largo plazo a cambio de mejores precios',
              consequences: 'La negociación puede resultar en una situación beneficiosa para ambas partes. Un compromiso a largo plazo proporciona seguridad al proveedor mientras que tú obtienes mejores precios.',
              score: 8
            },
            {
              id: 'ent1-b',
              text: 'Buscar proveedores alternativos que ofrezcan mejores precios',
              consequences: 'Diversificar proveedores es una estrategia sólida, pero cambiar completamente puede afectar la calidad del producto o servicio y requiere tiempo para establecer nuevas relaciones.',
              score: 7
            },
            {
              id: 'ent1-c',
              text: 'Transferir el aumento de costos a tus clientes subiendo los precios',
              consequences: 'Aunque mantiene tus márgenes, subir los precios puede afectar negativamente a las ventas y la satisfacción del cliente, especialmente si tus competidores no hacen lo mismo.',
              score: 5
            }
          ],
          marketData: 'Las empresas que mantienen relaciones a largo plazo con sus proveedores tienen un 25% menos de probabilidades de experimentar interrupciones en la cadena de suministro.'
        }
      ]
    };

    return [...commonScenarios, ...(specificScenarios[path] || [])];
  };

  const handleOptionSelect = (optionId: string) => {
    setSelectedOption(optionId);
  };

  const handleSubmit = () => {
    if (!selectedOption) return;
    setShowResults(true);
  };

  const handleNextScenario = () => {
    const currentIndex = scenarios.findIndex(s => s.id === currentScenario?.id);
    if (currentIndex < scenarios.length - 1) {
      setCurrentScenario(scenarios[currentIndex + 1]);
      setSelectedOption(null);
      setShowResults(false);
    } else {
      // Si es el último escenario, volver al primero
      setCurrentScenario(scenarios[0]);
      setSelectedOption(null);
      setShowResults(false);
    }
  };

  const getSelectedOption = () => {
    if (!currentScenario || !selectedOption) return null;
    return currentScenario.options.find(opt => opt.id === selectedOption);
  };

  if (loading || !currentScenario) {
    return (
      <div className="bg-white shadow-md rounded-lg p-6">
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 text-white">
        <h2 className="text-lg font-medium flex items-center">
          <Lightbulb className="h-5 w-5 mr-2" />
          Simulador de Toma de Decisiones IA
        </h2>
        <p className="mt-1 text-sm text-white/80">
          Enfrenta escenarios reales y aprende de las consecuencias de tus decisiones
        </p>
      </div>

      <div className="p-4 md:p-6">
        <div className="flex justify-between items-start mb-6">
          <h3 className="text-lg font-medium text-gray-900">{currentScenario.title}</h3>
          <button
            onClick={onChatWithMentor}
            className="inline-flex items-center text-accent hover:text-accent/90 text-sm"
          >
            <MessageSquare className="h-4 w-4 mr-1" />
            Consultar mentor
          </button>
        </div>

        <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4 mb-6">
          <p className="text-gray-800">{currentScenario.description}</p>
        </div>

        {!showResults ? (
          <>
            <h4 className="text-md font-medium text-gray-900 mb-4">¿Qué harías en esta situación?</h4>
            <div className="space-y-4">
              {currentScenario.options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleOptionSelect(option.id)}
                  className={`w-full text-left p-4 border rounded-lg transition-colors ${
                    selectedOption === option.id
                      ? 'border-accent bg-accent/5'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0 pt-0.5">
                      <div
                        className={`h-5 w-5 rounded-full border flex items-center justify-center ${
                          selectedOption === option.id
                            ? 'border-accent bg-accent text-white'
                            : 'border-gray-300'
                        }`}
                      >
                        {selectedOption === option.id && (
                          <CheckCircle className="h-3 w-3" />
                        )}
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{option.text}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleSubmit}
                disabled={!selectedOption}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  !selectedOption
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-accent hover:bg-accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent'
                }`}
              >
                Confirmar decisión
              </button>
            </div>
          </>
        ) : (
          <div>
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Análisis de tu decisión</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>{getSelectedOption()?.consequences}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mb-6 p-4 border border-gray-200 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700">Puntuación</h4>
              <div className="mt-2 flex items-center">
                <div className="text-3xl font-bold text-accent">{getSelectedOption()?.score}</div>
                <div className="ml-2 text-lg text-gray-500">/10</div>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {getSelectedOption()?.score >= 8
                  ? 'Excelente decisión. Has demostrado un gran entendimiento del problema.'
                  : getSelectedOption()?.score >= 6
                  ? 'Buena decisión. Hay algunos aspectos que podrías mejorar.'
                  : 'Esta decisión podría mejorarse. Revisa el análisis para entender por qué.'}
              </p>
            </div>
            
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-4">Otras opciones posibles</h4>
              <div className="space-y-4">
                {currentScenario.options
                  .filter(option => option.id !== selectedOption)
                  .map(option => (
                    <div key={option.id} className="p-4 border border-gray-200 rounded-lg">
                      <p className="text-sm font-medium text-gray-900">{option.text}</p>
                      <p className="mt-1 text-sm text-gray-500">{option.consequences}</p>
                      <div className="mt-2 flex items-center">
                        <span className="text-xs font-medium text-gray-500">Puntuación: {option.score}/10</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={handleNextScenario}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-accent hover:bg-accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
              >
                Siguiente escenario
                <ArrowRight className="ml-1 h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIDecisionMaking;